import React from 'react';
import {
  ChevronRight, ChevronUp, ChevronDown, ArrowUpDown,
  Eye, Edit2, Zap, Coffee, AlertTriangle
} from 'lucide-react';

export const formatDur = (s) => {
  if (!s || s <= 0) return '0h 0m';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

export const AdminSortTh = ({ label, field, sortField, sortDir, onSort }) => {
  const active = sortField === field;
  return (
    <th className="px-4 py-3 cursor-pointer select-none hover:text-white transition" onClick={() => onSort(field)}>
      <div className="flex items-center gap-1">
        <span className={active ? 'text-blue-400' : ''}>{label}</span>
        {active ? (
          sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-400" /> : <ChevronDown className="w-3 h-3 text-blue-400" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </div>
    </th>
  );
};

export const DriverMobileCard = ({ driver, driverStates, onSelect }) => {
  const st = driverStates[driver.driver_id];
  const isWorking = st?.current_state === 'WORK';
  const isResting = st?.current_state === 'REST';

  return (
    <div
      className="p-4 hover:bg-slate-700/30 cursor-pointer"
      onClick={() => onSelect(driver)}
      data-testid={`mobile-driver-card-${driver.driver_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
            <span className="text-white font-medium">
              {driver.users?.first_name?.[0]}{driver.users?.last_name?.[0]}
            </span>
          </div>
          <div>
            <p className="font-medium text-white text-sm">{driver.users?.first_name} {driver.users?.last_name}</p>
            <p className="text-xs text-slate-400">{driver.users?.email}</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
        <span className="text-xs text-slate-400">License: {driver.license_number || '-'}</span>
        <div className="flex items-center gap-2">
          {st && !st.error && (
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
              isWorking ? 'bg-red-500/20 text-red-400' : isResting ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
            }`}>
              {isWorking && <Zap className="w-2.5 h-2.5 inline mr-0.5" />}
              {isResting && <Coffee className="w-2.5 h-2.5 inline mr-0.5" />}
              {st.current_state}
              {isWorking && st.continuous_work_seconds > 0 && ` ${formatDur(st.continuous_work_seconds)}`}
            </span>
          )}
          <span className={`px-2 py-1 rounded-full text-xs ${
            driver.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600 text-slate-400'
          }`}>
            {driver.status}
          </span>
        </div>
      </div>
    </div>
  );
};

export const DriverTableRow = ({ driver, driverState: st, statesLoading, onView, onEdit }) => {
  const isWorking = st?.current_state === 'WORK';
  const isResting = st?.current_state === 'REST';

  return (
    <tr className="border-t border-slate-700 hover:bg-slate-700/30">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-medium">
                {driver.users?.first_name?.[0]}{driver.users?.last_name?.[0]}
              </span>
            </div>
            {st && !st.error && (
              <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-slate-800 ${
                isWorking ? 'bg-red-500' : isResting ? 'bg-emerald-500' : 'bg-slate-500'
              }`} />
            )}
          </div>
          <div>
            <p className="text-sm font-medium">{driver.users?.first_name} {driver.users?.last_name}</p>
            <p className="text-[10px] text-slate-400">{driver.users?.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {st && !st.error ? (
          <div>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
              isWorking ? 'bg-red-500/20 text-red-400' : isResting ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
            }`}>
              {isWorking && <Zap className="w-3 h-3 inline mr-0.5" />}
              {isResting && <Coffee className="w-3 h-3 inline mr-0.5" />}
              {st.current_state}
            </span>
            {st.compliance_status && st.compliance_status !== 'UNKNOWN' && st.compliance_status !== 'OK' && (
              <span className={`ml-1.5 text-[10px] ${st.compliance_status === 'WARNING' ? 'text-amber-400' : 'text-red-400'}`}>
                <AlertTriangle className="w-3 h-3 inline mr-0.5" />{st.compliance_status}
              </span>
            )}
          </div>
        ) : statesLoading ? (
          <span className="text-xs text-slate-500 animate-pulse">Loading...</span>
        ) : (
          <span className="text-xs text-slate-500">-</span>
        )}
      </td>
      {/* Hours Available */}
      <td className="px-4 py-3">
        {st && !st.error ? (
          <span className={`text-xs font-mono font-medium ${
            (st.hours_available_sec || 0) < 7200 ? 'text-red-400' :
            (st.hours_available_sec || 0) < 14400 ? 'text-amber-400' :
            'text-emerald-400'
          }`}>
            {formatDur(st.hours_available_sec)}
          </span>
        ) : <span className="text-xs text-slate-500">-</span>}
      </td>
      {/* Must Finish By */}
      <td className="px-4 py-3">
        {st?.must_finish_by ? (
          <div>
            <span className="text-xs font-mono text-amber-400">
              {new Date(st.must_finish_by).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
            <div className="text-[9px] text-slate-500">{st.must_finish_reason}</div>
          </div>
        ) : (
          <span className="text-xs text-slate-500">-</span>
        )}
      </td>
      {/* Can Start At */}
      <td className="px-4 py-3">
        {st?.current_state === 'REST' ? (
          st.can_start_at ? (
            <span className="text-xs font-mono text-blue-400">
              {new Date(st.can_start_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
          ) : (
            <span className="text-xs font-medium text-emerald-400">Ready</span>
          )
        ) : (
          <span className="text-xs text-slate-500">-</span>
        )}
      </td>
      {/* 24h Work */}
      <td className="px-4 py-3">
        {st && !st.error ? (
          <span className="text-xs text-white font-mono">{formatDur(st.work_24h_seconds)}</span>
        ) : (
          <span className="text-xs text-slate-500">-</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onView(driver)}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
            title="View Details"
            data-testid={`view-driver-${driver.driver_id}`}
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(driver)}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
            title="Edit"
            data-testid={`edit-driver-${driver.driver_id}`}
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};
